/**
 * Channel explainers (DESIGN UD-18, P1): what each channel measures and why it matters, in plain words, for
 * the popover shown wherever a channel ID or name appears, the Glossary view and the exports. Plain strings, no DOM.
 * None may contain a word matched by `BANNED_WORDS` (UX-WORD-01; test/unit/help-strings.test.ts). Sources: ENGINE-SPEC
 * §4 (the catalogue) and each channel's definition (`direction`, `minExposure`, `exposureUnit`). `direction` here must
 * agree with the definition; the test checks it.
 */
import type { ChannelExplainer } from '../../shared/help-types.ts';
import { CHANNEL_IDS, type ChannelId } from './types.ts';

export const CHANNEL_EXPLAINERS: Record<ChannelId, ChannelExplainer> = {
	'LX-01': {
		id: 'LX-01',
		measures:
			'How often words from a list of machine-favoured vocabulary appear per 1,000 words (“delve”, “tapestry”, “testament”), with heavy use of -ly adverbs added on top.',
		why: 'Language models over-use a small set of words and intensifying adverbs; human writers spread their choices more widely.',
		direction: 'higher',
		humanCauses:
			'Corporate, academic or marketing registers use the same words; the list was mined from 2023 to 2025 models and matches recent models less well.',
		needs: 'Abstains under 300 words.',
	},
	'LX-02': {
		id: 'LX-02',
		measures:
			'Template phrases per 1,000 words (“in today’s fast-paced world”, “it is worth noting that”, “plays a crucial role”).',
		why: 'Models reach for ready-made phrases to open, link and close; the same phrases recur across unrelated texts.',
		direction: 'higher',
		humanCauses: 'Business, student and press-release prose uses the same stock phrases.',
		needs: 'Abstains under 300 words.',
	},
	'LX-03': {
		id: 'LX-03',
		measures:
			'Brand-like names for ideas per 1,000 words: “the X Framework”, “the Y Method”, named concepts and growth terms, weighted by kind.',
		why: 'Models like to package advice as a named framework; most writers do not coin names for their own points.',
		direction: 'higher',
		humanCauses: 'Self-help, consulting and product writing coins frameworks on purpose.',
		needs: 'Abstains under 300 words.',
	},
	'PT-01': {
		id: 'PT-01',
		measures:
			'Em and en dashes per 1,000 words, with a dash pair (an aside set off by two dashes) counted double. A spaced hyphen is shown as evidence but not counted.',
		why: 'Models use dashes to join clauses far more than most writers, and dash pairs for asides.',
		direction: 'higher',
		humanCauses:
			'Some authors and house styles use dashes heavily by choice; some editors convert hyphens to dashes.',
		needs: 'Abstains under 250 words.',
	},
	'PT-02': {
		id: 'PT-02',
		measures:
			'How varied the punctuation is: the entropy of the mix of marks (full stops, commas, colons, dashes, question marks, …), normalised.',
		why: 'Model output tends to a narrow, regular mix of marks; human punctuation is more varied.',
		direction: 'lower',
		humanCauses:
			'Plain expository prose, legal text or minimalist style uses few marks on purpose.',
		needs: 'Abstains under 20 punctuation marks.',
	},
	'RH-01': {
		id: 'RH-01',
		measures:
			'Burstiness: how much sentence length varies within each section (the coefficient of variation of sentence length).',
		why: 'Models write sentences of similar length; human writers mix long and short sentences.',
		direction: 'lower',
		humanCauses:
			'Technical, legal and children’s prose keep sentences even; edited journalism is often regular too.',
		needs: 'Abstains under 5 sentences.',
	},
	'RH-02': {
		id: 'RH-02',
		measures:
			'Whether consecutive sentences alternate in length: the mean absolute difference between neighbouring sentence lengths, relative to the mean length.',
		why: 'Human rhythm alternates (a long sentence, then a short one); model output runs more evenly.',
		direction: 'lower',
		humanCauses: 'Lists, instructions and formal reports run even by design.',
		needs: 'Abstains under 6 sentences.',
	},
	'RH-03': {
		id: 'RH-03',
		measures:
			'How much paragraph length varies within each section (the coefficient of variation of paragraph length).',
		why: 'Models produce paragraphs of near-equal size; human paragraphs vary with what they carry.',
		direction: 'lower',
		humanCauses: 'House styles with a fixed paragraph length; web copy; dialogue-free narration.',
		needs: 'Abstains under 3 paragraphs.',
	},
	'ST-01': {
		id: 'ST-01',
		measures:
			'Contrastive frames per 1,000 words: “not X, but Y”, “it’s not about X; it’s about Y”.',
		why: 'The contrast frame is a favoured move of model prose for sounding incisive.',
		direction: 'higher',
		humanCauses:
			'Speech writing, sermons, opinion columns and advertising use the same frame on purpose.',
		needs: 'Abstains under 300 words.',
	},
	'ST-02': {
		id: 'ST-02',
		measures:
			'Triads per sentence: lists of exactly three items (“clear, compelling, and captivating”).',
		why: 'Models default to groups of three; writers vary between two, three and more.',
		direction: 'higher',
		humanCauses:
			'Rhetorical training teaches the rule of three; speeches and marketing copy lean on it.',
		needs: 'Abstains under 20 sentences.',
	},
	'ST-03': {
		id: 'ST-03',
		measures:
			'How monotonous the sentence openers are: the share of openers already used in the last 25 sentences, plus the shares of “This/These/That” openers and of participial openers (“Having …,”).',
		why: 'Model prose starts sentences the same few ways; human openers are more varied.',
		direction: 'higher',
		humanCauses:
			'Plain style guides recommend subject-first sentences; procedural and legal text repeats openers.',
		needs: 'Abstains under 6 sentences.',
	},
	'ST-04': {
		id: 'ST-04',
		measures:
			'Runs of three or more consecutive sentences that share the same opening frame, per sentence.',
		why: 'Anaphora (repeating a frame for effect) is a model habit when it wants emphasis.',
		direction: 'higher',
		humanCauses: 'Deliberate rhetorical anaphora in speeches, poetry and polemic.',
		needs: 'Abstains under 6 sentences.',
	},
	'SG-01': {
		id: 'SG-01',
		measures:
			'Whether the document’s chapters or scenes fall into two populations with different channel lean, tested by permutation.',
		why: 'A document with some machine-drafted and some human units looks bimodal; a single author looks unimodal.',
		direction: 'higher',
		humanCauses: 'Two human co-authors; a novel with two narrators; chapters written years apart.',
		needs: 'Abstains under 8 chapters or scenes.',
	},
	'NA-01': {
		id: 'NA-01',
		measures:
			'Narrative devices per 10 sentences: beats (“And then—”), cascades of short fragments, clause pivots (“But here’s the thing”); in a single-section text also a hook, a closer and “broetry” line breaks.',
		why: 'Models stage narration with a recognisable set of beats and pivots.',
		direction: 'higher',
		humanCauses: 'Thriller and blog styles use beats and fragments on purpose.',
		needs: 'Abstains under 8 sentences.',
	},
	'FM-01': {
		id: 'FM-01',
		measures:
			'The share of lines that are headings, numbered items or labels, plus the rate of imperative openers (“Consider …”, “Note that …”).',
		why: 'Models scaffold answers with headers, numbered steps and imperatives.',
		direction: 'higher',
		humanCauses: 'Manuals, recipes, syllabi and slide notes are built this way.',
		needs: 'Abstains under 4 lines.',
	},
	'SF-04': {
		id: 'SF-04',
		measures:
			'Pseudo-precise details per 1,000 words: exact times, counts and figures that read as invented (“post at 7:15 am”, “increase by 37%”).',
		why: 'Models add specific-sounding numbers to seem concrete.',
		direction: 'higher',
		humanCauses: 'Reports and journalism that quote real figures; schedules and logs.',
		needs: 'Abstains under 350 words.',
	},
	'DS-01': {
		id: 'DS-01',
		measures:
			'Sentences that open with a discourse scaffold (“Moreover,”, “Additionally,”, “In conclusion,”), per sentence.',
		why: 'Models signpost every transition; most writers let the sentences connect themselves.',
		direction: 'higher',
		humanCauses: 'Student essays, formal reports and writing taught to signpost.',
		needs: 'Abstains under 20 sentences.',
	},
	'DS-02': {
		id: 'DS-02',
		measures: 'Hedges per 1,000 words (“may”, “can help”, “often”, “it is worth noting”).',
		why: 'Model prose hedges to avoid committing; it runs two ways, as a very low rate is also unusual.',
		direction: 'higher',
		humanCauses:
			'Academic and scientific prose hedges by convention; cautious authors hedge by temperament.',
		needs: 'Abstains under 400 words.',
	},
	'DS-03': {
		id: 'DS-03',
		measures:
			'Apodictic units per sentence: absolutes (“always”, “never”, “undeniably”), modal passives (“must be seen as”) and maxims.',
		why: 'Models state conclusions as universal truths; writers usually qualify them.',
		direction: 'higher',
		humanCauses: 'Manifestos, advertising, sermons and polemic.',
		needs: 'Abstains under 15 sentences.',
	},
	'SF-05': {
		id: 'SF-05',
		measures:
			'Term-and-gloss pairs per 1,000 words: a term followed by its explanation in brackets or after a dash (“liminal (in-between)”).',
		why: 'Models gloss their own vocabulary for an imagined reader; writers assume theirs.',
		direction: 'higher',
		humanCauses: 'Textbooks, popular science and translations gloss on purpose.',
		needs: 'Abstains under 350 words.',
	},
	'MT-01': {
		id: 'MT-01',
		measures:
			'Eight marks of machine-translated English per 1,000 words, weighted: dropped subjects, source-language word order, calqued idioms, article errors, untranslated fragments and similar.',
		why: 'The translation axis: these marks are left by machine translation into English and are rare in English composed directly.',
		direction: 'higher',
		humanCauses:
			'Writers working in English as a second language; human translation that stays close to the source.',
		needs: 'Abstains under 350 words.',
	},
	'OB-01': {
		id: 'OB-01',
		measures:
			'Pipeline residue per 1,000 words: Markdown left in plain text, bracketed citation numbers, chat residue (“As an AI language model”), AI citation tokens (“oaicite”), and a prompt-like opening.',
		why: 'These are traces of a model pipeline rather than of style; one hit is near-conclusive, so the estimate is not shrunk toward the prior.',
		direction: 'higher',
		humanCauses: 'A Markdown document pasted as plain text; copied web text with footnote numbers.',
		needs: 'Abstains under 150 words.',
	},
	'SF-01': {
		id: 'SF-01',
		measures: 'Specific details per 1,000 words: digits, proper names and quotations.',
		why: 'Model prose is generic; it avoids names, numbers and direct quotation. Fewer details is the machine-like direction.',
		direction: 'lower',
		humanCauses: 'Reflective essays, poetry, allegory and any writing that is abstract by design.',
		needs: 'Abstains under 400 words.',
	},
	'SF-02': {
		id: 'SF-02',
		measures: 'Contractions per 1,000 words (“don’t”, “it’s”, “we’ll”).',
		why: 'Models avoid contractions unless asked; fewer contractions is the machine-like direction.',
		direction: 'lower',
		humanCauses:
			'Formal, legal and academic registers avoid contractions; some house styles forbid them.',
		needs: 'Abstains under 400 words.',
	},
	'SF-03': {
		id: 'SF-03',
		measures:
			'Typing slips per 1,000 words (“dont”, “teh”, missing apostrophes) and double spaces after full stops; duplicated words are shown as evidence only.',
		why: 'Model output has no slips; a text with none reads as machine-like. This is the channel most responsible for flagging edited human manuscripts.',
		direction: 'lower',
		humanCauses:
			'Professional editing and proofreading remove every slip; a clean manuscript scores here exactly like machine text.',
		needs: 'Abstains under 500 words.',
	},
	'TX-01': {
		id: 'TX-01',
		measures:
			'Colloquial texture per 1,000 words: fillers (“honestly,”, “you know”), sentence-initial “And”/“But”, rhetorical questions.',
		why: 'Models avoid the loose texture of speech; less of it is the machine-like direction.',
		direction: 'lower',
		humanCauses: 'Formal registers and edited prose remove fillers; some writers never use them.',
		needs: 'Abstains under 350 words.',
	},
	'SR-01': {
		id: 'SR-01',
		measures:
			'The share of four-word sequences that repeat within the last 500 words, estimated so that document length does not change it.',
		why: 'Models repeat their own phrasing at short range; writers notice and vary it.',
		direction: 'higher',
		humanCauses:
			'Refrains, repeated dialogue tags, technical prose with fixed terms, song-like structure.',
		needs: 'Abstains under 80 words.',
	},
	'PROC-01': {
		id: 'PROC-01',
		measures:
			'Verbatim fidelity: of the distinctive three-word openings that recur, how often the full six-word sequence recurs with them, as a ratio.',
		why: 'A model that reuses a motif copies it exactly; a writer who returns to a motif usually rephrases it.',
		direction: 'higher',
		humanCauses:
			'Deliberate refrains, epigraphs, chapter formulas; this channel is length-dependent until its priors come from corpus values.',
		needs: 'Abstains under 1,500 words.',
	},
	'PROC-02': {
		id: 'PROC-02',
		measures:
			'How uniform the chapter lengths are: the spread of chapter word counts (an upper bound on their coefficient of variation).',
		why: 'Generated manuscripts come in chapters of near-equal size; human chapters vary. More uniform is the machine-like direction.',
		direction: 'lower',
		humanCauses:
			'Serialised fiction with a fixed instalment length; a strict outline; an editor’s target length.',
		needs: 'Abstains under 6 chapters (or scenes when there are no chapters).',
	},
	'PROC-03': {
		id: 'PROC-03',
		measures:
			'“The way …” similes per 1,000 words (“the way a river finds the sea”), excluding non-simile uses.',
		why: 'This simile frame is a signature of model fiction; writers favour “like” and “as”.',
		direction: 'higher',
		humanCauses: 'An author’s own stylistic tic; translated prose.',
		needs: 'Abstains under 1,500 words.',
	},
	'PROC-04': {
		id: 'PROC-04',
		measures:
			'Negation-led antithesis per 1,000 words: sentence-initial “Not …” fragments and “not … but …” within a short span.',
		why: 'Models build emphasis by denying one thing and asserting another; it is a hallmark of model fiction.',
		direction: 'higher',
		humanCauses: 'Literary styles built on antithesis; sermons; aphoristic writers.',
		needs: 'Abstains under 1,500 words.',
	},
	'PROC-05': {
		id: 'PROC-05',
		measures:
			'Emphasis marks per 1,000 words: em-dashes and italicised spans, averaged as two sub-scores.',
		why: 'Models lean on dashes and italics for emphasis far more than most prose.',
		direction: 'higher',
		humanCauses: 'Authors who italicise thought or stress; house styles that favour dashes.',
		needs:
			'Abstains under 1,500 words; the italic part abstains when the format does not preserve emphasis.',
	},
	'PROC-06': {
		id: 'PROC-06',
		measures:
			'Two things: the share of very short (4 words or fewer) and very long (50 or more) sentences, and whether sentence length drifts with the chapter’s index across the document.',
		why: 'Model fiction mixes clipped fragments with long run-ons, and its cadence drifts as the generation proceeds.',
		direction: 'higher',
		humanCauses:
			'Fragment-heavy literary styles; long-sentence authors; a book whose later chapters were written in a different mood.',
		needs: 'Abstains under 200 sentences; the drift part needs 6 chapters or scenes.',
	},
	'PROC-07': {
		id: 'PROC-07',
		measures:
			'The share of substantial paragraphs (over 15 words) that end on a short sentence of 9 words or fewer: an epigram closer.',
		why: 'Models like to close a paragraph on a short punch line; most writers do it rarely.',
		direction: 'higher',
		humanCauses: 'Thriller and advertising styles; authors who end on a beat by habit.',
		needs: 'Abstains under 60 paragraphs.',
	},
	'PROC-08': {
		id: 'PROC-08',
		measures:
			'Template productivity: how often a five-word frame with one open slot recurs with a different word in the slot (“the weight of the [silence]”, “the weight of the [years]”).',
		why: 'Models re-fill their own templates; writers reuse a frame less and vary it more.',
		direction: 'higher',
		humanCauses:
			'Formulaic genres, liturgy and verse; this channel is length-dependent until its priors come from corpus values.',
		needs: 'Abstains under 8,000 words.',
	},
	'TR-01': {
		id: 'TR-01',
		measures:
			'A weighted count, per 1,000 words, of words and two-word phrases that machine translation into English prefers over what human translators and English-original writers choose, from a lexicon mined on paired human and machine translations of the same passages.',
		why: 'The translation axis: on paired translations, lexical preference was the one family with measurable signal; this channel reads the machine-leaning side of it.',
		direction: 'higher',
		humanCauses:
			'A human translator or writer who favours the same words; the lexicon was mined on Claude Sonnet 5.5 translations and excludes British and American spelling pairs, archaic forms and period dialogue tags by design.',
		needs: 'Abstains under 300 words.',
	},
	'TR-02': {
		id: 'TR-02',
		measures:
			'A weighted count, per 1,000 words, of words and two-word phrases that human translators and English-original writers prefer over machine translation, from the same paired lexicon as TR-01.',
		why: 'The human-leaning side of the translation axis: a text rich in these choices reads as human-made; a text poor in them reads more like machine translation.',
		direction: 'lower',
		humanCauses:
			'Plain or technical English with little idiom scores low here for ordinary reasons; the lexicon was mined on Claude Sonnet 5.5 translations and excludes spelling variety, archaic forms and period dialogue tags by design.',
		needs: 'Abstains under 300 words.',
	},
	'LX-04': {
		id: 'LX-04',
		measures:
			'How varied the vocabulary of the content words is, as the moving-average type-token ratio over a 50-word window (function words such as “the” and “of” are left out). Higher means fewer repeated content words.',
		why: 'In short texts, models cycle through synonyms where a writer would repeat the key word, so their vocabulary looks more varied than human prose of the same length.',
		direction: 'higher',
		humanCauses:
			'Writers who avoid repetition on purpose (an editor, a thesaurus habit, an academic or marketing register) score high for ordinary reasons; the reading holds only for short texts.',
		needs:
			'Scored from 150 to 999 words, and only with at least 50 content words; not scored at 1,000 words and over, where long human fiction varies its vocabulary as much as machine text.',
	},
};

export function explainerFor(id: ChannelId): ChannelExplainer {
	return CHANNEL_EXPLAINERS[id];
}

/** Every explainer in registry order (the Glossary view and the exports). */
export function allExplainers(): ChannelExplainer[] {
	return CHANNEL_IDS.map((id) => CHANNEL_EXPLAINERS[id]);
}
